import _ from "lodash";
import React, { forwardRef } from "react";
import classNames from "classnames";

import { useI18n } from "Hooks";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { Avatar, AvatarGroup } from "@toddle-edu/ds-web";
import { getAvatarColor } from "Utils/ColorHelper";
import {
  MicrophoneOutlined,
  PlaceholderFileOutlined,
  LinkColored,
} from "@toddle-edu/ds-icons";

import { localeParamsHelper, getTimeSince, getStyleStrippedText } from "Utils";
import { isBehaviourIncidentNotificationType } from "BehaviourManagement/modules/Utils";
import { getSpecifiedIcon } from "../../modules/Utils";

import { PARENT, STUDENT } from "Constants/stringConstants";
import { getShouldShowCourseDisplayName } from "LearningCourses/modules/Utils";

import classes from "./NotificationCard.scss";
import { isGlobalHeaderEnabled } from "AppComponents/NotificationCenterV2/modules/NotificationCenterV2Modules";
import { getIsClassAnnouncementEnabled } from "AppComponents/Announcements/modules/utils";

const styles = {
  stackContainer1: {
    left: "0",
    top: "0",
  },
  stackContainer2: {
    right: "0",
    top: "0",
  },
  stackContainer3: {
    top: "8px",
    left: "4px",
    right: "4px",
  },
  secondaryIconDimensions: {
    height: "20px",
    width: "20px",
  },
};

const getContextLabel = ({
  context,
  t,
  notificationType,
  circularCreatedIn,
}) => {
  if (isBehaviourIncidentNotificationType({ notificationType })) {
    const incidentDetails = _.head(context);
    const rootCategoryName = _.get(
      incidentDetails,
      "category.rootCategory.name"
    );

    if (rootCategoryName) {
      return `${incidentDetails.uid} - ${rootCategoryName}`;
    }

    return incidentDetails.uid;
  }

  if (
    notificationType === "CIRCULAR_ADDED" &&
    getIsClassAnnouncementEnabled() &&
    circularCreatedIn === "CLASS"
  ) {
    const courseData = _.first(context);
    const courseName = _.get(courseData, "title");
    return t("notifications:announcement_for_class", {
      className: courseName,
    });
  }

  //threre will be only one type in any notification
  const type = _.get(_.first(context), "type");

  switch (type) {
    case "student": {
      const studentsName = _.map(context, data =>
        getFormattedUserName({ user: data })
      );
      const studentsNameSize = _.size(studentsName);

      if (studentsNameSize < 3) {
        const studentsNameString = _.join(studentsName, ", ");
        return t("notifications:announcement_notif_context", {
          studentNames: studentsNameString,
        });
      }

      return t("notifications:announcement_notif_context_other", {
        firstStudent: studentsName[0],
        secondStudent: studentsName[1],
        studentCount: studentsNameSize - 2,
      });
    }
    default: {
      const entitiesTitle = _.map(context, entity => {
        const { title, name } = entity;
        // Decide whether to show learning course display name or not
        const shouldShowCourseDisplayName = getShouldShowCourseDisplayName();

        // Get course display name if applicable else fall back to title or name
        const courseDisplayName = shouldShowCourseDisplayName
          ? _.get(entity, "learningCourse[0].academicCourse.label") ||
            _.get(entity, "learningCourse[0].title") ||
            title ||
            name
          : title || name;
        return courseDisplayName;
      });
      const totalEntities = _.size(entitiesTitle);
      const contextText = _.head(entitiesTitle);
      return totalEntities > 1
        ? `${contextText} ${t("common:more_with_count", {
            count: totalEntities - 1,
          })}`
        : contextText;
    }
  }
};

const AnnouncementAvatar = props => {
  const { createdBy } = props;
  const { id, profileImage } = createdBy;
  const fullName = getFormattedUserName({ user: createdBy, type: "staff" });

  return (
    <Avatar
      id={id}
      name={_.toUpper(fullName)}
      src={profileImage}
      shade="light"
      size="medium"
      color={getAvatarColor(id)}
    />
  );
};

export const NotificationCard = (props, ref) => {
  const {
    data,
    userType,
    notificationDetails = {},
    onNotificationClicked,
    testId,
    behaviourNomenclatureConstants,
    tabIndex,
    currentIdx,
    selectedItem,
  } = props;

  const { t } = useI18n([
    "common",
    "project",
    "notifications",
    "journal",
    "assessmentEvaluation",
    "classRoom",
    "snp",
    "announcement",
  ]);

  const {
    type,
    contextData,
    createdBy,
    messageData,
    extensionData,
    createdAt,
    metadata,
    isRead,
    isNew,
  } = data;

  const { label, studentLabel, familyLabel } = notificationDetails || {};
  const { titleLocalizationKey, contentSubType, title, circularCreatedIn } =
    metadata || {};
  const { localeKey, tokens = [], entities = [] } = messageData || {};

  const {
    localeKey: extensionLocalizationKey,
    tokens: extensionTokens,
    users = [],
  } = extensionData || {};

  const {
    dsIcon,
    svgIcon,
    bgColor,
    secondaryIcon,
    stackedIcons = false,
    studentLabel: studentLabelFromSubCategory,
    familyLabel: familyLabelFromSubCategory,
  } = _.get(notificationDetails, "subCategories")
    ? getSpecifiedIcon({ metadata, notificationDetails, type, entities })
    : notificationDetails;

  const showAnnouncementAvatar =
    type === "CIRCULAR_ADDED" &&
    !_.isEmpty(createdBy) &&
    !getIsClassAnnouncementEnabled();

  const finalIcon = showAnnouncementAvatar ? (
    <AnnouncementAvatar createdBy={createdBy} />
  ) : (
    dsIcon || svgIcon
  );

  const { dsIcon: secondaryDsIcon, bgColor: secondaryIconBgColor } =
    secondaryIcon || {};
  const useStackedIcon = stackedIcons && _.size(entities) > 1;

  const showSecondaryIconBgColor = !_.isEmpty(secondaryIconBgColor);

  const iconStyles = {
    mainIcon: {
      backgroundColor: bgColor,
    },
    secondaryIcon: {
      backgroundColor: secondaryIconBgColor,
      ...(showSecondaryIconBgColor ? styles.secondaryIconDimensions : {}),
    },
  };

  const containerClasses = classNames({
    [classes.container]: true,
    [classes.notRead]: !isRead,
    [classes.containerV2]: isGlobalHeaderEnabled(),
    [classes.selected]:
      tabIndex === 0 && isGlobalHeaderEnabled() && selectedItem !== null,
  });

  const contextClasses = classNames({
    [classes.context]: true,
    [classes.contextRead]: isRead,
  });

  const messageClasses = classNames({
    [classes.message]: true,
    [classes.messageRead]: isRead,
    [classes.messageNotRead]: !isRead,
  });

  const extensionTextClasses = classNames({
    [classes.extensionText]: true,
    [classes.extensionTextRead]: isRead,
    [classes.extensionTextNotRead]: !isRead,
  });

  const durationTextClasses = classNames({
    [classes.duration]: true,
    [classes.durationRed]: isNew,
  });

  const handleNotificationClick = () => {
    onNotificationClicked(data);
  };

  const getContextLocaleKey = () => {
    const labelLocalizationKey = titleLocalizationKey
      ? `notifications:${titleLocalizationKey}`
      : undefined;

    switch (userType) {
      case STUDENT:
        return (
          studentLabelFromSubCategory ||
          studentLabel ||
          label ||
          labelLocalizationKey
        );
      case PARENT:
        if (type === "CHAT_MESSAGE_SENT") {
          return "notifications:notif_teacher_messaging_title";
        }
        return (
          familyLabelFromSubCategory ||
          familyLabel ||
          label ||
          labelLocalizationKey
        );
      default:
        if (contentSubType == "dp_pa" && type === "ASSIGNMENT_CREATE") {
          return "notifications:new_practical_activity";
        }
        if (contentSubType == "dp_ep" && type === "ASSIGNMENT_CREATE") {
          return "notifications:new_experimental_programme";
        }
        return labelLocalizationKey || label;
    }
  };
  const getNotificationMessage = () => {
    const messageLocaleKey = localeKey;
    if (!messageLocaleKey) {
      return "";
    }

    switch (type) {
      case "CIRCULAR_ADDED": {
        if (!_.isEmpty(tokens)) {
          const localeParam = localeParamsHelper({ params: tokens, t });
          return t(`notifications:${messageLocaleKey}`, localeParam);
        }
        return "";
      }

      default: {
        const localeParam = localeParamsHelper({ params: tokens, t });
        return t(`notifications:${messageLocaleKey}`, localeParam);
      }
    }
  };

  const getNotificationExtensionText = () => {
    const localeParam = localeParamsHelper({ params: extensionTokens, t });
    return t(`notifications:${extensionLocalizationKey}`, localeParam);
  };

  const renderNotificationThumbnail = () => {
    return (
      <div className={classes.thumbnailContainer}>
        {useStackedIcon ? (
          <>
            <div
              className={classes.stackedIconContainer}
              style={{ ...styles.stackContainer1, backgroundColor: bgColor }}
            />
            <div
              className={classes.stackedIconContainer}
              style={{ ...styles.stackContainer2, backgroundColor: bgColor }}
            />
            <div
              className={classes.stackedIconContainer}
              style={{ ...styles.stackContainer3, backgroundColor: bgColor }}
            >
              {finalIcon}
            </div>
          </>
        ) : (
          <>
            <div className={classes.iconContainer} style={iconStyles.mainIcon}>
              {finalIcon}
            </div>
            {secondaryDsIcon ? (
              <div
                className={classes.secondaryIcon}
                style={iconStyles.secondaryIcon}
              >
                {secondaryDsIcon}
              </div>
            ) : null}
          </>
        )}
      </div>
    );
  };

  const renderContext = () => {
    const context = contextData || {};

    if (
      type === "CIRCULAR_ADDED" &&
      circularCreatedIn === "GLOBAL" &&
      getIsClassAnnouncementEnabled()
    ) {
      return <div className={contextClasses}>{t(label)}</div>;
    }

    // if context is dynamic i.e. entities resolved from backend like courses, grades, yearGroups
    if (!_.isEmpty(context)) {
      const contextLabel = getContextLabel({
        t,
        context,
        notificationType: type,
        circularCreatedIn,
      });

      return <div className={contextClasses}>{contextLabel}</div>;
    }

    if (isBehaviourIncidentNotificationType({ notificationType: type })) {
      return (
        <div className={contextClasses}>
          {behaviourNomenclatureConstants.MODULE}
        </div>
      );
    }

    // if context is static or build using locale key and tokens
    let contextLabel = title;
    const contextLocaleKey = getContextLocaleKey();
    if (contextLocaleKey) {
      const localeParam = localeParamsHelper({ params: tokens, t });
      contextLabel = t(contextLocaleKey, localeParam);
    }

    return <div className={contextClasses}>{contextLabel}</div>;
  };

  const renderAvatar = () => {
    const commentType = _.get(extensionData, ["metaData", "commentType"], "");

    switch (commentType) {
      case "FILE_PLACEHOLDER": {
        return (
          <div className={classes.placeholderIconContainer}>
            <PlaceholderFileOutlined size={"xxx-small"} variant="link" />
          </div>
        );
      }

      case "LINK_PLACEHOLDER": {
        return (
          <div className={classes.placeholderIconContainer}>
            <LinkColored size={"small"} />
          </div>
        );
      }

      default: {
        return renderAvatarGroup({ users });
      }
    }
  };

  const renderAvatarGroup = ({ users }) => {
    if (_.isEmpty(users)) {
      return null;
    }
    return (
      <div className={classes.profileContainer}>
        <AvatarGroup size={"small"} shade={"light"} max={2}>
          {_.map(
            users,
            ({
              id,
              firstName = "",
              lastName = "",
              profileImage,
              userName = "",
            }) => {
              const name = _.isEmpty(userName)
                ? `${firstName} ${lastName}`
                : userName;
              return <Avatar key={id} name={name} src={profileImage} />;
            }
          )}
        </AvatarGroup>
      </div>
    );
  };

  const renderAudioNotificationExtension = () => {
    return (
      <div className={classes.audioNotificationExtensionContainer}>
        <MicrophoneOutlined size={"xxx-small"} />
        <span className={extensionTextClasses}>
          {getNotificationExtensionText()}
        </span>
      </div>
    );
  };

  const renderExtension = () => {
    if (_.isEmpty(extensionData)) {
      return null;
    }

    const commentType = _.get(extensionData, ["metaData", "commentType"], "");

    return (
      <div className={classes.extensionContainer}>
        {renderAvatar()}

        {!_.isEmpty(commentType) && commentType === "AUDIO" ? (
          renderAudioNotificationExtension()
        ) : (
          <div className={extensionTextClasses} dir="auto">
            {getNotificationExtensionText()}
          </div>
        )}
      </div>
    );
  };

  const handleKeyDown = event => {
    const code = _.get(event, "code");
    if (code === "Enter" || code === "Space") {
      handleNotificationClick();
    }
  };
  return (
    <div
      className={containerClasses}
      onClick={handleNotificationClick}
      tabIndex={tabIndex ? tabIndex : 0}
      data-test-id={`${testId}`}
      ref={el => {
        if (ref.current && isGlobalHeaderEnabled()) {
          ref.current[currentIdx] = el;
        }
      }}
      data-elm="notif-card"
      onKeyDown={handleKeyDown}
    >
      <div className={classes.innerContainer}>
        <div className={classes.leftContainer}>
          {renderNotificationThumbnail()}
        </div>
        <div className={classes.rightContainer}>
          <div className={classes.header}>
            {renderContext()}
            <div className={classes.durationContainer}>
              <div className={durationTextClasses}>
                {getTimeSince(createdAt, t)}
              </div>
              {isNew ? <div className={classes.redDot} /> : null}
            </div>
          </div>
          <div
            dangerouslySetInnerHTML={{
              __html: getStyleStrippedText({
                text: getNotificationMessage(),
                type: "SANITIZE_DANGER_HTML_PARAGRAPH",
              }),
            }}
            className={messageClasses}
          />
          {renderExtension()}
        </div>
      </div>
    </div>
  );
};

NotificationCard.displayName = "NotificationCard";

NotificationCard.propTypes = {};

export default React.memo(forwardRef(NotificationCard));
